import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import 'url-search-params-polyfill';
import Lightbox from 'react-image-lightbox';
import 'react-image-lightbox/style.css';
import { ReCAPTCHA } from 'react-google-recaptcha';
import workflowUtil from '../util/workflowUtil';
import DateOfBirthInput from '../components/DateOfBirthInput';
import { isCanadianClient, isValidActivationCode } from '../util/gearOrderUtil';
import ClientActivationMissingMsg from './ClientActivationMissingMsg';

const grecaptchaSiteKey = process.env.REACT_APP_GOOGLE_RECAPTCHA_SITEKEY;

/*
 * Removes any non-digits from the requisition number
 */
const cleanRequisitionNumber = (reqNum) => {
	return reqNum?.replace(/\D/g, '') ?? '';
};

/**
 * Checks if the GDX ID field is required.
 * GDX ID is required when the requisition number is less than 8 characters long, but over 4,000,000.
 * The 4 million check is so entering a Gear Order Id won't require a client ID.
 * 
 * @param reqNum The requisition number
 */
const isClientIdRequired = (reqNum) => {
	return reqNum && cleanRequisitionNumber(reqNum).length < 8 && Number(reqNum) > 4000000;
}

export default function PrcRequisitionSearch({ userInfo, setLoading }) {
	let history = useHistory();
	let { reqNum } = useParams();

	const [dateOfBirth, setDateOfBirth] = useState('');
	const [requisitionNumber, setRequisitionNumber] = useState(reqNum || '');
	const [gdxId, setGdxId] = useState('');
	const [showReqImage, setShowReqImage] = useState(false);
	const [showGdxIdImage, setShowGdxIdImage] = useState(false);
	const [searchMessage, setSearchMessage] = useState(null);
	const [errorMessage, setErrorMessage] = useState(null);
	const [orderableItemNotice, setOrderableItemNotice] = useState(null);
	const [captchaValue, setCaptchaValue] = useState(null);
	const [requireCaptcha, setRequireCaptcha] = useState(true);
	const [practitionerOptions, setPractitionerOptions] = useState(null);
	const [grecaptchaObject, setGrecaptchaObject] = useState(window.grecaptchaObject);
	const [scriptCount, setScriptCount] = useState(0);
	const [showClientIdInput, setShowClientIdInput] = useState(isClientIdRequired(reqNum));
	const reqInputRef = useRef(null);

	/**
	 * Reset the ReCaptcha
	 */
	const resetReCaptcha = () => {
		if (grecaptchaObject && grecaptchaObject.reset) {
			grecaptchaObject.reset();
		}
	};

	/**
	 * Attempt to store the window.grecaptcha value in state.
	 * If the value is not available, try again after 500 ms
	 */
	const loadGrecaptchaObject = () => {
		// Setting grecaptchaObject doesn't always force a re-render because of how it is a reference to window.grecaptcha
		// The scriptCount value is to make sure state is refreshed and a re-render happens each time this method is called
		setScriptCount((prev) => prev + 1);
		const newObj = window.grecaptcha;

		if (!newObj || !newObj.reset) {
			setTimeout(() => {
				loadGrecaptchaObject();
			}, 500);
		} else {
			setGrecaptchaObject(newObj);
		}
	};

	/**
	 * Load the Google ReCaptcha script, which will create the window.grecaptcha object
	 */
	const loadRecaptchaScript = () => {
		const script = document.createElement('script');
		script.src = '//www.google.com/recaptcha/api.js';
		script.type = 'text/javascript';
		document.head.appendChild(script);
		// script.onload = () => {
			loadGrecaptchaObject();
		// };
	};

	/**
	 * Load the Google ReCaptcha script and object when the component mounts.
	 */
	useEffect(() => {
		if (!window.grecaptcha) {
			loadRecaptchaScript();			
		} else {
			loadGrecaptchaObject();
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	/**
	 * If reCaptcha is not required (Asking for practitioner selection) but the requisitionNumber
	 *  or dateOfBirth is changed, reset the reCaptcha and make it required. This forces the use
	 *  of the reCaptcha any time we are searching for a new requisition number.
	 */
	useEffect(() => {
		setRequireCaptcha((prevRequire) => {
			if (!prevRequire) {
				setGdxId('');
				setPractitionerOptions(null);
				resetReCaptcha();
			}

			return true;
		});
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [requisitionNumber, dateOfBirth]);

	const handleRecaptchaChange = (value) => {
		// null is passed in when the recaptcha expires. Change that to 'false' so we know the recaptcha has been attempted.
		setCaptchaValue(value || false);
	};

	const checkShowClientId = () => {
		setShowClientIdInput(isClientIdRequired(requisitionNumber));
	}

	/**
	 * Updates the requisition number.
	 *
	 * @param e the DOM event
	 */
	const updateRequisitionNumber = (e) => {
		setRequisitionNumber(e.currentTarget.value);
	};

	/**
	 * Updates the date of birth.
	 *
	 * @param dob the new Date of Birth value
	 */
	const updateDateOfBirth = (dob) => {
		setDateOfBirth(dob);
	};

	/**
	 * Updates the practitioner GDX Id, forcing uppercase.
	 *
	 * @param e the DOM event
	 */
	const updateGdxId = (e) => {
		setGdxId(e.target.value.toUpperCase());
	};

	/**
	 * Finds an order. If an order is found with a matching requisition and patient date of birth, then the user is
	 * routed to the order. Otherwise, a message will be displayed indicating no matching order was found.
	 *
	 * For newer, 8-digit, requisitions we do not ask for the GDX ID up front. If the requisition could be registered by
	 *  multiple practitioners, the list of possible practitioners is returned and the user will need to make a selection
	 *  before proceeding.
	 *
	 * @param e the DOM event
	 */
	const findOrder = (e) => {
		e.preventDefault();
		setSearchMessage(null);
		setOrderableItemNotice(null);
		setErrorMessage(null);
		setPractitionerOptions(null);

		if (requireCaptcha && !captchaValue) {
			setCaptchaValue(false);
			return;
		}

		/* 
		 * Client ID is checked when the req# field is blurred. 
		 * This condition is to handle when the form is submitted without the req# field being 
		 *  blurred (Maybe [enter] was pressed to submit the form).
		 */
		if (isClientIdRequired(requisitionNumber) && !gdxId) {
			// Blur the req# field so the client ID field will display
			if (reqInputRef?.current?.blur) {
				reqInputRef.current.blur();
			}
			return;
		}

		// encode the req number in case it has a '#' character
		let url = `${process.env.REACT_APP_GEAR_REST_URL}/orderId?requisitionNumber=${encodeURIComponent(requisitionNumber)}&dateOfBirth=${dateOfBirth}`;
		if (gdxId) {
			url += `&gdxId=${gdxId}`;
		}
		if (requireCaptcha && captchaValue) {
			url += `&recaptcha=${captchaValue}`;
		}

		(setLoading && setLoading(true));

		fetch(url, {
			mode: 'cors',
			credentials: 'include',
			method: 'GET',
			timeout: 1000,
			headers: {
				Accept: 'application/json',
				'Content-Type': 'text/plain'
			}
		})
			.then((response) => {
				if (response.ok) {
					return response.json();
				} else {
					return Promise.reject({
						response: response
					});
				}
			})
			.then((json) => {
				if (json.practitionerOptions && json.practitionerOptions.length) {
					setGdxId(json.practitionerOptions[0].gsdlNo);
					setPractitionerOptions(json.practitionerOptions);
					setRequireCaptcha(false);
					setCaptchaValue(null);
					return;
				}

				let gearOrder = json;

				// SCR-7143: Do not allow patients to register international requisitions
				if (gearOrder?.practitioner?.office?.reqLayoutId === 3 && !isCanadianClient(gearOrder)) {
					setSearchMessage(
						<span>
							Online activation of requisition <b>{requisitionNumber}</b> is not required.
						</span>
					);
					return;
				}

				// Get the order's ID. 'new' will be used when the ID = 0.
				let gearOrderId = 'new';
				if (gearOrder && gearOrder.id) {
					gearOrderId = gearOrder.id;
				}

				/*
				 * SCR-6905: If the practitioner has started the order go directly to the confirmation page. 
				 * Otherwise, go to the first incomplete step that is applicable for patients.
				 */
				let nextStep = 'confirm';
				if ((!workflowUtil.isClientOrder(gearOrder) || gearOrder?.physicianInTheLoop) && gearOrder?.workflow?.steps) {
					// find the first applicable step in the order's workflow
					let firstIncompleteStep = gearOrder.workflow.steps.find((step, index) => {
						return (
							step && !step.completed && workflowUtil.isStepValidForUser(step, userInfo, gearOrder)
						);
					});
					const firstStepMetaData = workflowUtil.getWorkflowMetaDataById((firstIncompleteStep || {}).id);
					if (firstStepMetaData && firstStepMetaData.route) {
						nextStep = firstStepMetaData.route;
					}
				}

				const orderPractitioner = gearOrder.practitioner || {};
				// Navigate to the order
				history.push(
					`/order/${gearOrderId}/${nextStep}?dateOfBirth=${dateOfBirth}&requisitionNumber=${requisitionNumber}&gdxId=${orderPractitioner.gsdlNo}`
				);
				return;
			})
			.catch((e) => {
				setCaptchaValue(null);
				if (e.response) {
					return e.response.text().then((text) => {
						if (text) {
							let json = JSON.parse(text);
							if (json.error && json.error === 'RecaptchaInvalidException') {
								setErrorMessage('Error verifying reCAPTCHA. Please try again.');
								return Promise.resolve();
							} else if (json?.error === 'ClientActivationMissingException') {
								setSearchMessage(<ClientActivationMissingMsg activationNumber={requisitionNumber} />)
								return;
							} else if (json.message) {
								if (json?.error === 'RequisitionActivationRetiredException') { 
									setOrderableItemNotice(json.message);
									return;
								}
								setSearchMessage(json.message);
								return Promise.resolve();
							}
						}

						// There was no message
						if (e.response.status === 401) {
							history.push({ pathname: '/login' });
							return;
						} else if (e.response.status === 404 && isValidActivationCode(requisitionNumber)) {
							setSearchMessage(<ClientActivationMissingMsg activationNumber={requisitionNumber} />);
							return;
						} else if (e.response.status === 404 || e.response.status === 400) {
							let msg = practitionerOptions ? "We could not find a requisition associated to your practitioner with that number. Please check the number and contact Genova Diagnostics if you believe it is valid."
									: "We could not find a requisition associated with that number. Please check the number and contact Genova Diagnostics if you believe it is valid.";
							setSearchMessage(msg);
						} else {
							return Promise.reject('Unexpected error');
						}
					});
				} else {
					return Promise.reject('Unexpected error');
				}
			})
			.catch((e) => {
				// Catch and handle any unexpected errors
				setErrorMessage('The network is currently unavailable. Please call us at 800-522-4762.');
			})
			.finally(() => {
				resetReCaptcha();
				(setLoading && setLoading(false));
			});
	};

	return (
		<>
			{errorMessage && (
				<div className="alert alert-danger">{errorMessage}</div>
			)}
			{searchMessage && (
				<div className="alert alert-warning">{searchMessage}</div>
			)}
			{orderableItemNotice && (
				<div dangerouslySetInnerHTML={{ __html: orderableItemNotice }}></div>
			)}

			<div className="row">
				<div className="col-sm-11 col-md-10">
					<form onSubmit={findOrder}>
						<div className="row display-flex space-bottom">
							<div className="col-sm-6 col-xs-8">
								<label className="control-label" htmlFor="requisitionSearchBox">
									Activation Number
								</label>
								<input
									className="form-control"
									id="requisitionSearchBox"
									type="search"
									required
									value={requisitionNumber}
									onChange={updateRequisitionNumber}
									onBlur={checkShowClientId}
									autoComplete="off"
									ref={reqInputRef}
								></input>
								<button
									type="button"
									className="btn btn-link btn-unstyled"
									tabIndex="-1"
									onClick={() => setShowReqImage(true)}
								>
									<em>Where do I find my number?</em>
								</button>
							</div>

							<div className="col-sm-6 col-xs-12">
								<span className="space-top visible-xs-block"></span>
								<label className="control-label" htmlFor="patient-dateOfBirth">
									Date Of Birth
								</label>
								<DateOfBirthInput onChange={updateDateOfBirth} displayDateString={true} required={true} hideInputLables={true} />
							</div>
						</div>
						{showClientIdInput && (
							<div className="row space-bottom">
								<div className="col-xs-6">
									<label className="control-label" htmlFor="practitioner-gdxId">
										Practitioner GDX ID
									</label>
									<input
										className="form-control"
										id="practitioner-gdxId"
										type="search"
										required
										value={gdxId}
										onChange={updateGdxId}
										autoComplete="off"
									></input>
									<button
										type="button"
										className="btn btn-link btn-unstyled"
										tabIndex="-1"
										onClick={() => setShowGdxIdImage(true)}
									>
										<em>Where do I find this ID?</em>
									</button>
								</div>
							</div>
						)}

						{practitionerOptions && (
							<div className="form-group space-bottom">
								<h4>We need one more piece of information</h4>
								<label className="control-label" htmlFor="practitionerOptions">
									Please Select Your Practitioner
								</label>
								<select className="form-control" id="practitionerOptions" value={gdxId} onChange={updateGdxId}>
									{practitionerOptions.map(function (practitioner) {
										return (
											<option key={practitioner.gsdlNo} value={practitioner.gsdlNo}>
												{practitioner.firstName} {practitioner.lastName}, {practitioner.degree} (
												{practitioner.gsdlNo})
											</option>
										);
									})}
								</select>
							</div>
						)}
						{/* Use class='hidden' instead of removing from DOM so grecaptchaObject.reset() does not cause errors */}
						<div className={'row space-bottom' + (!requireCaptcha ? ' hidden' : '')}>
							<div className={'col-xs-12 col-md-6 gear-recaptcha' } style={{minHeight: requireCaptcha ? '83px': '0'}}>
								<ReCAPTCHA
									grecaptcha={grecaptchaObject}
									sitekey={grecaptchaSiteKey}
									onChange={handleRecaptchaChange}
									style={{ display: 'inline-block', outline: captchaValue === false ? '1px solid red' : '' }}
									scriptcount={scriptCount}
								/>
							</div>
						</div>
						
						<div className="row">
							<div className="col-md-6">
								<button type="submit" className="btn btn-primary radius margin-clear btn-block">
									<strong>Continue</strong>
								</button>
							</div>
						</div>

						{showReqImage && (
							<Lightbox
								mainSrc="/images/gear/Req-Activate-Numbers.jpg"
								imageTitle="Genova Diagnostics Requisition Form"
								onCloseRequest={() => setShowReqImage(false)}
								imageLoadErrorMessage="Requisition image failed to load"
							/>
						)}
						{showGdxIdImage && (
							<Lightbox
								mainSrc="/images/gear/ReqAndGdxId.png"
								imageTitle="Genova Diagnostics Requisition Form"
								onCloseRequest={() => setShowGdxIdImage(false)}
								imageLoadErrorMessage="Requisition image failed to load"
							/>
						)}
					</form>
				</div>
			</div>
		</>
	);
}
